import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { trackActivity } from 'Infrastructure/Service/AnalyticsService'

const BEAT_INTERVAL_MS = 15000

function getAnalyticsHost() {
  const hostname = window.location.hostname.replace(/^viewer\./, '')
  return window.location.protocol + '//analytics.' + hostname
}

function detectDeviceType() {
  const ua = navigator.userAgent
  if (/iPad|Tablet|Playbook|Silk/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'tablet'
  if (/iPhone|Android.*Mobile|Mobile|iPod/i.test(ua)) return 'phone'
  return 'pc'
}

function detectBrowser() {
  const ua = navigator.userAgent
  if (/Edg\//i.test(ua)) return 'Edge'
  if (/Chrome\//i.test(ua)) return 'Chrome'
  if (/Firefox\//i.test(ua)) return 'Firefox'
  if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) return 'Safari'
  if (/Opera|OPR\//i.test(ua)) return 'Opera'
  return 'Other'
}

function detectOS() {
  const ua = navigator.userAgent
  if (/Windows/i.test(ua)) return 'Windows'
  if (/Mac OS X/i.test(ua)) return 'macOS'
  if (/Android/i.test(ua)) return 'Android'
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS'
  if (/Linux/i.test(ua)) return 'Linux'
  return 'Other'
}

function buildClientMeta() {
  return {
    device: detectDeviceType(),
    browser: detectBrowser(),
    os: detectOS(),
    resolution: window.screen.width + 'x' + window.screen.height,
    pixelRatio: window.devicePixelRatio,
    touchPoints: navigator.maxTouchPoints,
    language: navigator.language
  }
}

function sendHeartbeat(url, body) {
  const controller = new AbortController()
  setTimeout(() => controller.abort(), 5000)
  fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: controller.signal
  }).catch(() => {})
}

export function useAnalyticsHeartbeat() {
  const viewerIdRef = useRef(null)
  const lastModeRef = useRef('view')
  const modeRef = useRef('view')
  const userRef = useRef(null)
  const beatUrlRef = useRef('')
  const metaSentRef = useRef(false)
  const lastUserKeyRef = useRef('')

  const mode = useSelector((state) => state.ViewportReducer?.mode || 'view')
  const user = useSelector((state) => state.UserReducer?.user || null)

  modeRef.current = mode
  userRef.current = user

  useEffect(() => {
    const id = localStorage.getItem('sw_analytics_id') || (Math.random().toString(36).slice(2) + Date.now().toString(36))
    localStorage.setItem('sw_analytics_id', id)
    viewerIdRef.current = id

    const host = getAnalyticsHost()
    beatUrlRef.current = host + '/api/analytics/viewers/heartbeat?id=' + encodeURIComponent(id)

    function beat() {
      if (!beatUrlRef.current || !viewerIdRef.current) return

      const body = {}
      const currentUser = userRef.current
      const userKey = currentUser ? (currentUser.email || currentUser.name || '') : ''

      if (!metaSentRef.current) {
        body.meta = buildClientMeta()
        if (currentUser && currentUser.email) body.email = currentUser.email
        if (currentUser && currentUser.name) body.name = currentUser.name
        metaSentRef.current = true
        lastUserKeyRef.current = userKey
      } else if (userKey && userKey !== lastUserKeyRef.current) {
        if (currentUser && currentUser.email) body.email = currentUser.email
        if (currentUser && currentUser.name) body.name = currentUser.name
        lastUserKeyRef.current = userKey
      }

      sendHeartbeat(beatUrlRef.current, body)
    }

    beat()
    const interval = setInterval(beat, BEAT_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const currentMode = mode === 'edition' ? 'edition' : 'view'
    if (currentMode !== lastModeRef.current) {
      const type = currentMode === 'edition' ? 'enter_edit' : 'exit_edit'
      trackActivity(type)
      lastModeRef.current = currentMode
    }
  }, [mode])
}
